import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import {
  END_USER_FIND_ALTERNATIVES_ROUTE,
  MY_DAY_MEAL_ROUTE,
} from '../../services/routes';
import FullWidthMealCard from '../meal/full_width_meal_card';
import { linkWithQueryParams } from '../../services/urls';
import {
  useAddUserMyDayActionRecordMutation,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useLazyGetFullMealQuery,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  isMealCompleted,
  isMealSkipped,
  mealCompletedActionRecord,
  mealSkippedActionRecord,
  mealUnskippedActionRecord,
} from '../../services/my_day';
import { mergeMyDayActionRecords } from '../../services/programmes';
import { scaleMeal } from '../../operations/meal_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import MyDayPlaceholderCard from './my_day_placeholder_card';
import { isMealBackgroundProcessingSelector } from '../../features/recipes/recipes_slice';

const MyDayMealCard = ({ mealID, parentID, calendarDay }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const isBackgroundProcessing = useSelector((state) =>
    isMealBackgroundProcessingSelector(state, mealID),
  );

  const { data: distilledMeal, isSuccess: isMealSuccess } =
    useGetDistilledMealByIDAndParentQuery({ id: mealID, parentID });

  const title = distilledMeal?.recipes[0]?.title;
  const servings = distilledMeal?.recipes[0]?.servings;
  const mealType = ((distilledMeal?.recipes || [])[0]?.mealTypes || [])[0];

  const calories =
    distilledMeal?.derivedNutrition?.totalNutritionPerServing?.calories;

  const { data: currentSpaceMembership, isSuccess: isSpaceMembershipSuccess } =
    useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const { data: currentProgrammeEnrollment, isSuccess: isEnrollmentSuccess } =
    useCurrentProgrammeEnrollmentQuery();

  const {
    data: myDayActionRecordsByEnrollment,
    isSuccess: isMyDayActionRecordsByEnrollmentSuccess,
  } = useGetMyDayActionRecordsByEnrollmentQuery(
    currentProgrammeEnrollment?.id,
    {
      skip: !currentProgrammeEnrollment?.id,
    },
  );

  const {
    data: myDayActionRecordsBySpaceMembership,
    isSuccess: isMyDayActionRecordsBySpaceMembershipSuccess,
  } = useGetMyDayActionRecordsBySpaceMembershipQuery(
    currentSpaceMembership?.id,
    {
      skip: !currentSpaceMembership?.id,
    },
  );

  const hasLoadedSuccessfully =
    isMealSuccess &&
    isSpaceMembershipSuccess &&
    isEnrollmentSuccess &&
    (!currentProgrammeEnrollment || isMyDayActionRecordsByEnrollmentSuccess) &&
    (!currentProgrammeEnrollment ||
      isMyDayActionRecordsBySpaceMembershipSuccess);

  const [addUserMyDayActionRecord] = useAddUserMyDayActionRecordMutation();

  const [updateMeal] = useUpdateMealMutation();

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const isCompleted = isMealCompleted(
    mealID,
    currentProgrammeEnrollment?.id,
    myDayActionRecords || [],
  );

  const isSkipped = isMealSkipped(
    mealID,
    currentProgrammeEnrollment?.id,
    myDayActionRecords || [],
  );

  const onClickSkip = () => {
    addUserMyDayActionRecord(
      mealSkippedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const onClickUnskip = () => {
    addUserMyDayActionRecord(
      mealUnskippedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const onClickComplete = () => {
    addUserMyDayActionRecord(
      mealCompletedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const [scalingInProgress, setScalingInProgress] = useState(false);

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const onChangeScaleToServings = useCallback(
    async (scaleToServings) => {
      const { data: fullMeal } = await lazyGetMeal(mealID);
      const scaleToServingsNumber = Number(scaleToServings) || 1;
      if (scaleToServingsNumber !== servings) {
        const scaleFactor = scaleToServingsNumber / (servings || 1);
        setScalingInProgress(true);
        try {
          await scaleMeal(
            fullMeal,
            scaleFactor,
            scaleToServingsNumber,
            customerFoodDataID,
            false,
            updateMeal,
            dispatch,
            lazyGetMeal,
          );
        } finally {
          setScalingInProgress(false);
        }
      }
    },
    [customerFoodDataID, dispatch, lazyGetMeal, mealID, servings, updateMeal],
  );

  if (!hasLoadedSuccessfully) {
    return <MyDayPlaceholderCard />;
  }

  if (hasLoadedSuccessfully && !distilledMeal) {
    return null;
  }

  return (
    <FullWidthMealCard
      skipEnabled={!!currentProgrammeEnrollment}
      completeEnabled={!!currentProgrammeEnrollment}
      alternativesEnabled
      title={title}
      imageUrl={distilledMeal.recipes[0].imageUrl}
      mealTypes={distilledMeal.recipes[0].mealTypes}
      calories={calories}
      servings={servings}
      onClick={() => {
        navigate(
          formatRoute(MY_DAY_MEAL_ROUTE, {
            parentID,
            mealID,
          }),
        );
      }}
      onClickSkip={onClickSkip}
      onClickUnskip={onClickUnskip}
      onClickFindAlternatives={() =>
        navigate(
          linkWithQueryParams(
            formatRoute(END_USER_FIND_ALTERNATIVES_ROUTE, {
              calendarDay,
            }),
            { mealType, mealId: distilledMeal?.id },
          ),
        )
      }
      onClickComplete={onClickComplete}
      servingsEnabled
      onChangeScaleToServings={onChangeScaleToServings}
      isSkipped={isSkipped}
      isCompleted={isCompleted}
      scalingInProgress={scalingInProgress}
      isBackgroundProcessing={isBackgroundProcessing}
    />
  );
};

MyDayMealCard.propTypes = {
  mealID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
  calendarDay: PropTypes.string.isRequired,
};

export default MyDayMealCard;
